import axios from "axios";
import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { AuthModal } from "@/components/auth";
import { PRODUCTS } from "@/components/shop";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["Orders", "Enquiries", "Feedback", "Customers", "Stock"];
const ORDER_STATUSES = ["created", "paid", "shipped", "delivered"];
const ENQUIRY_STATUSES = ["new", "contacted", "closed"];

const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const inr = (paise) => `₹${(paise / 100).toLocaleString("en-IN")}`;

export const AdminDashboard = () => {
  const [user, setUser] = useState(undefined);
  const [authOpen, setAuthOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [tab, setTab] = useState("Orders");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadTab = async (t) => {
    setLoading(true);
    if (t === "Stock") {
      try {
        const { data } = await axios.get(`${API}/stock`);
        setRows(PRODUCTS.map(p => ({ ...p, in_stock: data[p.id] !== false })));
      } catch {
        toast.error("Could not load data");
      } finally {
        setLoading(false);
      }
      return;
    }
    const endpoints = { Orders: "orders", Enquiries: "enquiries", Feedback: "feedbacks", Customers: "users" };
    try {
      const { data } = await axios.get(`${API}/admin/${endpoints[t]}`);
      setRows(data);
    } catch {
      toast.error("Could not load data");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
      const w = await axios.get(`${API}/admin/sales-weekly`);
      setWeekly(w.data);
    } catch {}
  };
  const maxRevenue = Math.max(...weekly.map(w => w.revenue_paise), 1);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        if (data.role === "admin") setUser(data);
        else setUser(null);
      } catch { setUser(null); }
    };
    check();
  }, []);

  useEffect(() => {
    if (user) { loadStats(); loadTab(tab); }
  }, [user, tab]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}`, { status });
      setRows(rows.map(r => r.id === orderId ? { ...r, status } : r));
      toast.success(`Order marked ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const updateEnquiryStatus = async (enquiryId, status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${enquiryId}`, { status });
      setRows(rows.map(r => r.id === enquiryId ? { ...r, status } : r));
      toast.success(`Enquiry marked ${status}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const sendDigest = async () => {
    try {
      await axios.post(`${API}/admin/feedback-digest`, {});
      toast.success("Digest sent", { description: "Check jamvanya25@gmail.com for the summary." });
    } catch {
      toast.error("Could not send digest");
    }
  };

  const toggleStock = async (productId, inStock) => {
    try {
      await axios.patch(`${API}/admin/stock/${productId}`, { in_stock: inStock });
      setRows(rows.map(r => r.id === productId ? { ...r, in_stock: inStock } : r));
      toast.success(inStock ? "Back in stock" : "Marked sold out");
    } catch {
      toast.error("Stock update failed");
    }
  };

  if (user === undefined) return <div className="admin-wrap"><p className="admin-note">Loading…</p></div>;
  if (!user) {
    return (
      <div className="admin-wrap">
        <Toaster position="top-right"/>
        <div className="admin-locked" data-testid="admin-locked">
          <h1>Jamvanya Admin</h1>
          <p>This area is for the Jamvanya team only. Please sign in with an admin account.</p>
          <button className="button button-primary" onClick={() => setAuthOpen(true)} data-testid="admin-login-button">Admin login</button>
          <a href="/" className="text-link" data-testid="admin-back-link">Back to store</a>
        </div>
        {authOpen && <AuthModal onClose={() => setAuthOpen(false)} onAuth={(u) => { if (u.role === "admin") { setUser(u); } else { toast.error("This account is not an admin"); } }}/>}
      </div>
    );
  }

  return (
    <div className="admin-wrap" data-testid="admin-dashboard">
      <Toaster position="top-right"/>
      <header className="admin-top">
        <span className="admin-brand">Jamvanya <em>Admin</em></span>
        <div className="admin-top-right">
          <span data-testid="admin-user-email">{user.email}</span>
          <a href="/" className="text-link" data-testid="admin-store-link">View store</a>
        </div>
      </header>
      {stats && (
        <div className="admin-stats" data-testid="admin-stats">
          <div className="stat-card"><b data-testid="stat-orders">{stats.orders}</b><span>Orders</span></div>
          <div className="stat-card"><b data-testid="stat-revenue">{inr(stats.revenue_paise)}</b><span>Paid revenue</span></div>
          <div className="stat-card"><b data-testid="stat-enquiries">{stats.enquiries}</b><span>Enquiries</span></div>
          <div className="stat-card"><b data-testid="stat-feedbacks">{stats.feedbacks}</b><span>Reviews · {stats.avg_rating}/5</span></div>
          <div className="stat-card"><b data-testid="stat-customers">{stats.customers}</b><span>Customers</span></div>
        </div>
      )}
      {weekly.length > 0 && (
        <div className="sales-chart" data-testid="sales-chart">
          <span className="chart-title">Weekly sales · last 8 weeks</span>
          <div className="chart-bars">
            {weekly.map(w => (
              <div className="chart-col" key={w.week} data-testid="chart-bar">
                <span className="chart-val">{w.revenue_paise > 0 ? inr(w.revenue_paise) : ""}</span>
                <div className="chart-bar" style={{ height: `${Math.max(4, (w.revenue_paise / maxRevenue) * 110)}px` }} title={`${w.week}: ${inr(w.revenue_paise)} · ${w.count} order(s)`}/>
                <small>{w.week}</small>
              </div>
            ))}
          </div>
        </div>
      )}
      <nav className="admin-tabs">
        {TABS.map(t => (
          <button key={t} className={tab === t ? "active" : ""} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </nav>
      {loading ? <p className="admin-note">Loading…</p> : rows.length === 0 ? <p className="admin-note" data-testid="admin-empty">Nothing here yet.</p> : (
        <div className="admin-table" data-testid="admin-table">
          {tab === "Orders" && rows.map(o => (
            <div className="admin-row" key={o.id} data-testid="admin-order-row">
              <div><b>{o.name}</b><small>{o.email} · {o.phone || "-"}</small><small>{o.address}, PIN {o.pincode}</small></div>
              <div className="admin-row-items">{(o.items || []).map(i => `${i.quantity} x ${i.name} ${i.size}`).join(", ")}</div>
              <b>{inr(o.amount)}</b>
              <small>{fmtDate(o.created_at)}</small>
              <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} data-testid={`order-status-${o.id.slice(0, 8)}`}>
                {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
          {tab === "Enquiries" && rows.map(e => (
            <div className="admin-row" key={e.id} data-testid="admin-enquiry-row">
              <div><b>{e.name}</b><small>{e.email} · {e.phone || "-"}</small></div>
              <span className={`order-status ${e.enquiry_type === "order" ? "paid" : ""}`}>{e.enquiry_type}</span>
              <div className="admin-row-items">{e.quantity || "-"}</div>
              <div className="admin-row-items">{e.message || "-"}</div>
              <small>{fmtDate(e.created_at)}</small>
              <a className="reply-btn" href={`mailto:${e.email}?subject=${encodeURIComponent("Re: Your Jamvanya enquiry")}&body=${encodeURIComponent(`Hi ${e.name},\n\nThank you for reaching out to Jamvanya Private Limited.\n\n\n\nWarm regards,\nJamvanya Team\n+91 7208656816`)}`} data-testid={`reply-${e.id.slice(0, 8)}`}>Reply</a>
              <select value={e.status || "new"} onChange={ev => updateEnquiryStatus(e.id, ev.target.value)} data-testid={`enquiry-status-${e.id.slice(0, 8)}`}>
                {ENQUIRY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
          {tab === "Feedback" && (
            <button className="button button-primary" style={{ justifySelf: "start", marginBottom: 6 }} onClick={sendDigest} data-testid="send-digest-button">Email me the weekly digest now</button>
          )}
          {tab === "Feedback" && rows.map(f => (
            <div className="admin-row" key={f.id} data-testid="admin-feedback-row">
              <div><b>{f.name || "Guest"}</b><small>{fmtDate(f.created_at)}</small></div>
              <span>Website {f.website_rating}/5 · Product {f.product_rating}/5 · Order {f.order_rating}/5</span>
              <div className="admin-row-items">{f.comment || "-"}</div>
            </div>
          ))}
          {tab === "Customers" && rows.map(u => (
            <div className="admin-row" key={u.user_id} data-testid="admin-customer-row">
              <div><b>{u.name || "-"}</b><small>{u.email}</small></div>
              <span className={`order-status ${u.role === "admin" ? "paid" : ""}`}>{u.role || "customer"}</span>
              <span>{u.provider || "password"}</span>
              <small>{fmtDate(u.created_at)}</small>
            </div>
          ))}
          {tab === "Stock" && rows.map(p => (
            <div className="admin-row" key={p.id} data-testid="admin-stock-row">
              <div><b>{p.name}</b><small>{p.size} · ₹{p.price}</small></div>
              <span className={`order-status ${p.in_stock ? "paid" : ""}`} data-testid={`stock-label-${p.id}`}>{p.in_stock ? "In stock" : "Sold out"}</span>
              <button className={`stock-btn ${p.in_stock ? "" : "out"}`} onClick={() => toggleStock(p.id, !p.in_stock)} data-testid={`stock-toggle-${p.id}`}>{p.in_stock ? "Mark sold out" : "Mark in stock"}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
